<script lang="ts">
  /**
   * LOGIN PAGE — Full enterprise implementation
   *
   * Features:
   * 1. Zod schema validation (runs BEFORE any API call)
   * 2. Per-field error display (red text under each input)
   * 3. Loading spinner on submit button while API is in flight
   * 4. Global error banner for server-side errors (wrong password, etc.)
   * 5. Auto-redirect to /dashboard on success (handled by RouteGuard)
   * 6. Toast notification on successful login
   *
   * FLOW:
   * User types → clicks Submit → Zod validates → if valid, authState.login()
   * → on success, authState.isAuthenticated becomes true → RouteGuard's $effect
   * detects this and redirects to /dashboard automatically.
   */

  import { push } from 'svelte-spa-router';
  import { loginSchema, type LoginFormData } from '../auth.schema';
  import { authState } from '../auth.state.svelte';
  import { toastState } from '../../../shared/toast/toast.state.svelte';
  import Button from '../../../shared/components/Button.svelte';
  import Input from '../../../shared/components/Input.svelte';
  import type { ZodError } from 'zod';

  // ---- Form State (Svelte 5 Runes) ----

  // The raw form values the user types into
  let email = $state('');
  let password = $state('');

  // Per-field validation errors (populated by Zod)
  let fieldErrors = $state<Record<string, string>>({});

  // ---- Form Submission Handler ----

  async function handleSubmit(e: Event) {
    e.preventDefault(); // Prevent browser from doing a full page reload

    // Step 1: Clear previous errors
    fieldErrors = {};
    authState.clearError();

    // Step 2: Validate with Zod
    // safeParse returns { success, data, error } instead of throwing
    const result = loginSchema.safeParse({ email, password });

    if (!result.success) {
      // Zod validation failed — extract per-field errors
      // Zod's error.flatten() gives us { fieldErrors: { email: [...], password: [...] } }
      const flat = result.error.flatten();
      const errors: Record<string, string> = {};

      for (const [key, messages] of Object.entries(flat.fieldErrors)) {
        if (messages && messages.length > 0) {
          errors[key] = messages[0]; // Show only the first error per field
        }
      }

      fieldErrors = errors;
      return; // Stop here — don't call the API with invalid data
    }

    // Step 3: Zod validation passed — call the auth service
    const success = await authState.login(result.data);

    if (success) {
      // Toast notification + redirect (RouteGuard handles the redirect automatically,
      // but we can also push explicitly for immediate feedback)
      toastState.success(`Selamat datang, ${authState.user?.fullName}!`);
      push('/dashboard');
    }
    // If login failed, authState.error will be set and displayed below
  }
</script>

<!-- 
  LAYOUT: Full-screen centered card on dark background.
  Login page does NOT use DashboardLayout (no sidebar/header needed).
-->
<div class="min-h-screen flex items-center justify-center bg-surface-900 px-4">
  <div class="w-full max-w-md">

    <!-- Brand Header -->
    <div class="text-center mb-8">
      <div class="text-5xl mb-4">🏢</div>
      <h1 class="text-3xl font-bold text-surface-100">Enterprise Dashboard</h1>
      <p class="text-surface-500 mt-2">Masukkan kredensial Anda untuk melanjutkan</p>
    </div>

    <!-- Login Card -->
    <div class="bg-surface-800 border border-surface-700 rounded-2xl p-8 shadow-2xl">

      <!-- Server-side Error Banner (e.g., "Email atau password salah") -->
      {#if authState.error}
        <div class="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
          <span class="text-red-400 text-lg">❌</span>
          <div>
            <p class="text-sm text-red-400 font-medium">Login Gagal</p>
            <p class="text-xs text-red-400/80 mt-0.5">{authState.error}</p>
          </div>
        </div>
      {/if}

      <!-- The Login Form -->
      <form onsubmit={handleSubmit} class="flex flex-col gap-5">

        <!-- Email Field -->
        <Input
          label="Email"
          type="email"
          placeholder="admin@demo.com"
          bind:value={email}
          error={fieldErrors.email}
          disabled={authState.isLoading}
        />

        <!-- Password Field -->
        <Input
          label="Password"
          type="password"
          placeholder="••••••••"
          bind:value={password}
          error={fieldErrors.password}
          disabled={authState.isLoading}
        />

        <!-- Submit Button -->
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={authState.isLoading}
        >
          {authState.isLoading ? 'Memproses...' : 'Masuk'}
        </Button>
      </form>

      <!-- Demo Credentials Hint -->
      <div class="mt-6 p-4 bg-surface-700/50 rounded-lg">
        <p class="text-xs text-surface-500 font-medium mb-2">🔑 Demo Credentials:</p>
        <div class="grid grid-cols-2 gap-2 text-xs text-surface-400">
          <div>
            <p class="font-medium text-surface-300">Admin:</p>
            <p>admin@demo.com</p>
            <p>admin123</p>
          </div>
          <div>
            <p class="font-medium text-surface-300">User:</p>
            <p>user@demo.com</p>
            <p>user123</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <p class="text-center text-surface-600 text-xs mt-6">
      Enterprise Dashboard v1.0 — Svelte 5 + Tailwind v4
    </p>
  </div>
</div>
